import { OpenerSettings, OpenerType } from "@common/schemas";
import {
  Dialog,
  DialogContent,
  DialogHeading,
  DialogDescription,
  DialogNext,
  DialogDismiss,
  DialogClose,
} from "./Dialog";
import { useEffect, useRef, useState } from "react";
import { autoUpdate, useFloating } from "@floating-ui/react";
import { ElementTracker } from "@/lib/element";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverDescription,
  PopoverDismiss,
  PopoverHeading,
  PopoverNext,
  PopoverTrigger,
} from "./Popover";
import { Captions, Minimize, Volume1, XIcon } from "lucide-react";
import { FindVisibleElementResult } from "@/lib/element";

import styles from "./Intro.module.css";
import { ShadowDomContainer } from "../ShadowDomContainer";

type IntroProps = {
  type: OpenerType;
  data: OpenerSettings;
  onStartTour: () => void;
  onPostponeTour: () => void;
  onCloseTour: () => void;
};

type EditorActions = { onNext: () => void; onPostpone: () => void; onClose?: () => void };

function Tooltip({
  selector,
  title,
  body,
  position,
  nextLabel,
  dismissLabel,
  onNext,
  onPostpone,
}: OpenerSettings & EditorActions) {
  const elementTrackerRef = useRef<ElementTracker>(new ElementTracker());
  const [target, setTarget] = useState<Element>();
  const floating = useFloating({
    open: !!target,
    whileElementsMounted: autoUpdate,
    elements: {
      reference: target,
    },
  });

  useEffect(() => {
    let tracker: ElementTracker;

    if (selector) {
      const popoverDelayInMs = 5_000;
      tracker = elementTrackerRef.current;
      tracker.trackElement(
        selector,
        (result: FindVisibleElementResult) => {
          if (!result.element) {
            setTarget(undefined);
          } else {
            setTarget(result.element);
          }
        },
        popoverDelayInMs
      );
    }

    return () => {
      tracker.stop();
    };
  }, [selector]);

  if (!target) return null;

  const box = floating.elements.reference?.getBoundingClientRect();
  const borderRadius = Number.parseFloat(getComputedStyle(target).borderRadius);
  const anchorStyles = {
    top: box?.top,
    left: box?.left,
    height: box?.height,
    width: box?.width,
    borderRadius,
  };

  return (
    <Popover placement={position} initialOpen modal>
      <PopoverTrigger asChild>
        <div
          style={anchorStyles}
          className={styles.anchor}
          ref={(el) => {
            floating.refs.setFloating(el);
          }}
        />
      </PopoverTrigger>
      <PopoverContent>
        <ShadowDomContainer>
          <div className={styles.popoverContent}>
            <PopoverHeading>{title}</PopoverHeading>
          </div>
          <PopoverDescription>{body}</PopoverDescription>
          <PopoverNext style={{ width: "100%" }} onClick={onNext}>
            {nextLabel}
          </PopoverNext>
          <PopoverDismiss style={{ width: "100%" }} onClick={onPostpone}>
            {dismissLabel}
          </PopoverDismiss>
        </ShadowDomContainer>
      </PopoverContent>
      xw
    </Popover>
  );
}

function Modal({ title, body, name, company, media, onNext, onPostpone }: OpenerSettings & EditorActions) {
  const [open, setOpen] = useState(true);

  const handleNext = () => {
    setOpen(false);
    onNext();
  };

  const handleDismiss = () => {
    setOpen(false);
    onPostpone();
  };
  return (
    <Dialog open={open}>
      <DialogContent>
        <ShadowDomContainer>
          <div className={styles.contentWrapper}>
            <div className={styles.overview}>
              {media && media.type && media.url && <img src={media.url} alt={title} />}
              <h4>Hi, I'm {name}</h4>
              <p>Your guide in {company}</p>
              <ul className={styles.overviewControls}>
                <li>
                  <Volume1 className={styles.controlsIcon} />
                  <span>Enable audio</span>
                </li>
                <li>
                  <Minimize className={styles.controlsIcon} />
                  <span>Use to minimize</span>
                </li>
                <li>
                  <Captions className={styles.controlsIcon} />
                  <span>Manage subtitles</span>
                </li>
              </ul>
            </div>
            <div className={styles.contentBody}>
              <div>
                <DialogHeading>{title}</DialogHeading>
                <DialogDescription>{body}</DialogDescription>
              </div>
              <div style={{ marginTop: 16 }}>
                <DialogNext onClick={handleNext}>Start Tour</DialogNext>
                <DialogDismiss onClick={handleDismiss}>Maybe later</DialogDismiss>
              </div>
            </div>
          </div>
        </ShadowDomContainer>
      </DialogContent>
    </Dialog>
  );
}

function Intro({ type, data, onStartTour, onPostponeTour, onCloseTour }: IntroProps) {
  const nextLabel = data.nextLabel || "Start Tour";
  const dismissLabel = data.dismissLabel || "Maybe Later";

  if (type === "modal") {
    return (
      <Modal
        title={data.title}
        body={data.body}
        name={data.name}
        company={data.company}
        media={data.media}
        onNext={onStartTour}
        onPostpone={onPostponeTour}
        nextLabel={nextLabel}
        dismissLabel={dismissLabel}
      />
    );
  }

  if (type === "tooltip") {
    return (
      <Tooltip
        selector={data.selector}
        title={data.title}
        body={data.body}
        position={data.position}
        nextLabel={nextLabel}
        dismissLabel={dismissLabel}
        onNext={onStartTour}
        onPostpone={onPostponeTour}
      />
    );
  }

  return null;
}

export default Intro;
